/**
 * DOM glue for the Listen ⇄ Read toggle (§7.5). Bridges between the timing-map
 * anchor space (block key / paragraph pid + offset) and the on-screen text, for
 * BOTH manuscript formats via format-specific adapters:
 *
 *  - draftjs: Draft.js renders each block wrapper with `data-block="true"` and
 *    `data-offset-key="{blockKey}-0-0"`; the leading segment is the block key
 *    that `chapterAudioText`/`timing.sentences[].anchor` use.
 *  - tiptap: the pid extension renders each pid block as `<p data-pid="{pid}">`
 *    (see components/sceneEditor/tiptapExtensions.js); `timing.sentences[].anchor`
 *    encodes that same `pid` as its `startKey`.
 *
 * The reverse index (`startMsForTextPosition`) matches purely on those keys, so
 * it is format-agnostic — only the DOM read (which attribute holds the key)
 * differs, which is exactly what these adapters isolate.
 *
 * All helpers are defensive: if the expected attributes aren't found they return
 * empty/null/no-op, so the toggle degrades to chapter-start / no-scroll rather
 * than throwing.
 */
const HEADER_OFFSET_PX = 80; // ignore content scrolled under the fixed header
const READ_JUMP_HIGHLIGHT_HOLD_MS = 1000;

// --- format adapters: how to select anchor elements and read their key ---
const ADAPTERS = {
  draftjs: {
    selector: '[data-block="true"][data-offset-key]',
    keyOf: el => {
      const raw = el && el.getAttribute && el.getAttribute('data-offset-key');
      return raw ? raw.split('-')[0] : null;
    }
  },
  tiptap: {
    selector: '[data-pid]',
    keyOf: el =>
      (el && el.getAttribute && el.getAttribute('data-pid')) || null
  }
};

const adapterFor = format => ADAPTERS[format] || ADAPTERS.draftjs;

/** Anchor elements of the rendered chapter, in document order. */
const anchorElements = (root, adapter) =>
  Array.from((root || document).querySelectorAll(adapter.selector));

/** Ordered anchor keys of the rendered chapter (for the reverse index). */
export const getOrderedKeys = (root = document, format = 'draftjs') => {
  const adapter = adapterFor(format);
  return anchorElements(root, adapter)
    .map(adapter.keyOf)
    .filter(Boolean);
};

/**
 * The reader's current text position: the first anchor element whose bottom is
 * below the header line (i.e. the block at the top of the viewport). Returns
 * `{key, offset}` or null when nothing is found.
 */
export const getTextPosition = (root = document, format = 'draftjs') => {
  const adapter = adapterFor(format);
  const els = anchorElements(root, adapter);
  for (const el of els) {
    const rect = el.getBoundingClientRect ? el.getBoundingClientRect() : null;
    if (rect && rect.bottom > HEADER_OFFSET_PX) {
      const key = adapter.keyOf(el);
      if (key) return {key, offset: 0};
    }
  }
  const first = els[0];
  const key = first && adapter.keyOf(first);
  return key ? {key, offset: 0} : null;
};

/**
 * Listen → Read: scroll the block/paragraph matching `anchor.startKey` into view
 * and flash a brief highlight. No-op when the anchor/element is missing (keeps
 * position). Returns true if it scrolled, false otherwise.
 */
export const scrollToAnchor = (anchor, root = document, format = 'draftjs') => {
  if (!anchor || anchor.startKey == null) return false;
  const adapter = adapterFor(format);
  const el = anchorElements(root, adapter).find(
    b => adapter.keyOf(b) === anchor.startKey
  );
  if (!el) return false;
  if (el.scrollIntoView) {
    el.scrollIntoView({behavior: 'smooth', block: 'center'});
  }
  // Self-contained flash (no external CSS dependency); the class is also toggled
  // as a styling hook. Reuses the karaoke highlight colour for consistency.
  if (el.style) {
    // Capture the PRISTINE background once (in a data attr), so a rapid second
    // call within the window doesn't capture the highlight colour as "previous"
    // and leave the block stuck highlighted.
    if (el.dataset && el.dataset.brAudioPrevBg == null) {
      el.dataset.brAudioPrevBg = el.style.backgroundColor || '';
    }
    if (el.classList) el.classList.add('br-audio-jump-highlight');
    el.style.transition = 'background-color 0.4s ease';
    el.style.backgroundColor = 'rgba(255, 224, 138, 0.6)';
    setTimeout(() => {
      if (!el.style) return;
      el.style.backgroundColor =
        (el.dataset && el.dataset.brAudioPrevBg) || '';
      if (el.dataset) delete el.dataset.brAudioPrevBg;
      if (el.classList) el.classList.remove('br-audio-jump-highlight');
    }, READ_JUMP_HIGHLIGHT_HOLD_MS);
  }
  return true;
};
